import { makeAutoObservable, runInAction } from "mobx";
import { CraNodeCoding } from "../../coding/models/CraNodeCoding";
import {
  isCraWayVisibleThroughFilters,
  isNodeVisibleByAttributes,
  isNodeVisibleByTreatment,
  isLifeCycleStateVisibleThroughFilters,
  isVisibleByScore,
} from "../utils/apply-filters";
import { Score, TreatmentType } from "@keita/lanepatrol-sdk";
import {
  CraAttributeFilter,
  AttributeSorting,
  emptyHlaAttributeFilter,
  HlaAttributeFilter,
  ScoreFilters,
} from "../../map/models/MapFilters";
import { RootStore } from "../../shared/stores/RootStore";
import { CraNode, Treatment } from "../../cra/models/CraNode";
import { LifeCycleStage } from "../../cra/models/LifeCycleStage";
import { CraWay } from "../../cra/models/CraWay";
import {
  getDefaultAverageDailyTraffic,
  getDefaultAverageRoadSpeed,
  HlaWay,
} from "../../hla/models/HlaWay";
import { HlaLane, HlaWayAdjacentFeatures } from "../../hla/models/HlaLane";

type craWayId = string;

export class FilterStore {
  rootStore;

  showCra = true;
  craWayFilters: craWayId[] = [];
  lifeCycleStageFilters: LifeCycleStage[] = [];
  craScoreFilters: ScoreFilters = {};
  treatmentFilters: TreatmentType[] = [];
  craAttributeFilters: CraAttributeFilter[] = [];

  showHla = true;
  hlaAttributeFilters = emptyHlaAttributeFilter();
  hlaVerifiedFilters: boolean[] = [];
  hlaScoreFilters: ScoreFilters = {};

  filteredHlaWays: HlaWay[] = [];
  filteredCraWays: CraWay[] = [];
  filteredNodes: CraNode[] = [];
  filteredHlaDistance = 0;
  filteredCraDistance = 0;

  attributeSorting = AttributeSorting.Alphabetic;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    this.resetCraFilters();
    this.resetHlaFilters();
    runInAction(() => {
      this.showHla = true;
      this.showCra = true;
      this.attributeSorting = AttributeSorting.Alphabetic;
      this.filteredCraWays = [];
      this.filteredNodes = [];
      this.filteredHlaWays = [];
      this.filteredHlaDistance = 0;
      this.filteredCraDistance = 0;
    });
  }

  resetHlaFilters() {
    runInAction(() => {
      this.hlaScoreFilters = {};
      this.hlaVerifiedFilters = [];
      this.hlaAttributeFilters = emptyHlaAttributeFilter();
    });

    this.filterHlaWays();
  }

  resetCraFilters() {
    runInAction(() => {
      this.craAttributeFilters = [];
      this.craWayFilters = [];
      this.lifeCycleStageFilters = [];
      this.craScoreFilters = {};
      this.treatmentFilters = [];
    });
    if (this.rootStore.craForecastStore.isForecastModeActivated()) {
      this.forceCodedLifeCycleStageFilters(this.lifeCycleStageFilters);
    }

    this.filterCraWays();
  }

  get originalOrForecastedCraWays(): CraWay[] {
    const { craForecastStore } = this.rootStore;
    return (
      craForecastStore.computedValues?.craWays ?? this.craWaysAvailableToUser
    );
  }

  get originalOrForecastedNodes(): Map<string, CraNode[]> {
    const { craForecastStore } = this.rootStore;
    return (
      craForecastStore.computedValues?.craNodes ??
      this.nodesAvailableInCurrentMode
    );
  }

  get originalOrForecastedAllNodeCodingsByWayId(): Map<
    string,
    Map<string, CraNodeCoding>
  > {
    const { craForecastStore, codingStore } = this.rootStore;
    return (
      craForecastStore.computedValues?.allNodeCodingsByWayId ??
      codingStore.allNodeCodingsByWayId
    );
  }

  get nodesAvailableInCurrentMode(): Map<string, CraNode[]> {
    const { craNodeStore } = this.rootStore;
    const availableNodes = new Map<string, CraNode[]>();
    for (const way of this.craWaysAvailableToUser) {
      const nodes = craNodeStore.nodesByWayId.get(way.id);
      if (nodes) {
        availableNodes.set(way.id, nodes);
      }
    }

    return availableNodes;
  }

  get craWaysAvailableToUser(): CraWay[] {
    const { craWayStore } = this.rootStore;

    return craWayStore.ways.filter((way) => this.isCraWayAvailableToUser(way));
  }

  filterHlaWays() {
    const { hlaStore } = this.rootStore;

    var filteredDistance = 0;

    const filtersWays = hlaStore.getWays().filter((way: HlaWay) => {
      if (!this.showHla) return false;

      if (!this.filterHlaWay(way)) return false;

      var nValidLanes = 0;
      if (way.frontFacingLane && this.filterHlaLane(way.frontFacingLane)) {
        nValidLanes++;
        filteredDistance += way.lengthM;
      }
      if (way.backFacingLane && this.filterHlaLane(way.backFacingLane)) {
        nValidLanes++;
        filteredDistance += way.lengthM;
      }
      return nValidLanes > 0;
    });

    runInAction(() => {
      this.filteredHlaWays = filtersWays;
      this.filteredHlaDistance = filteredDistance;
    });

    hlaStore.updateMapSource();
  }

  filterCraWays() {
    runInAction(() => {
      this.filteredCraWays = this.originalOrForecastedCraWays.filter((way) =>
        this.isCraWayVisible(way),
      );
    });
    this.filterNodes();

    const { mapStore } = this.rootStore;
    mapStore.craMapSource?.onStoreUpdate();
    mapStore.plannedTrajectoryMapSource?.onStoreUpdate();
  }

  filterNodes() {
    const allNodes = this.originalOrForecastedNodes;
    const filteredNodes: CraNode[] = [];

    this.filteredCraWays.forEach((way) => {
      const nodes = allNodes.get(way.id) ?? [];
      filteredNodes.push(
        ...nodes.filter(
          (s) =>
            this.isNodeVisibleByScore(s.score) &&
            this.isNodeVisibleByAttributes(way.id, s.id!) &&
            this.isNodeVisibleByTreatment(s.treatments),
        ),
      );
    });

    runInAction(() => {
      this.filteredNodes = filteredNodes;
    });

    this.calculateFilteredDistance();

    const { mapStore } = this.rootStore;
    mapStore.craMapSource?.onStoreUpdate();
  }

  private calculateFilteredDistance() {
    runInAction(() => {
      this.filteredCraDistance = this.filteredNodes.reduce(
        (acc, node) => acc + node.distanceToNext,
        0,
      );
    });
  }

  setAttributeSorting(value: AttributeSorting) {
    runInAction(() => {
      this.attributeSorting = value;
    });
  }

  filterHlaLane(lane: HlaLane) {
    if (!this.showHla) return false;
    const attrFilters = this.hlaAttributeFilters;

    const testAdjacentFeatures = (
      features: HlaWayAdjacentFeatures,
    ): boolean => {
      // Hard segregation
      if (
        attrFilters.hardSegregation.length > 0 &&
        !attrFilters.hardSegregation.includes(features.hardSegregation as any)
      )
        return false;

      // Road lane
      if (
        attrFilters.roadLane.length > 0 &&
        !attrFilters.roadLane.includes(features.roadLane as any)
      )
        return false;

      // Number of lanes
      if (
        attrFilters.numberOfLanes.length > 0 &&
        !attrFilters.numberOfLanes.includes(features.numberOfLanes as any)
      )
        return false;

      // Parking
      if (
        attrFilters.vehicleParking.length > 0 &&
        !attrFilters.vehicleParking.includes(features.vehicleParking as any)
      )
        return false;

      // Sidewalk
      if (
        attrFilters.sidewalk.length > 0 &&
        !attrFilters.sidewalk.includes(features.sidewalk as any)
      )
        return false;

      // Object or level change
      if (
        attrFilters.objectOrLevelChange.length > 0 &&
        !attrFilters.objectOrLevelChange.includes(
          features.objectOrLevelChange as any,
        )
      )
        return false;

      // Severe hazard
      if (
        attrFilters.severeHazard.length > 0 &&
        !attrFilters.severeHazard.includes(features.severeHazard as any)
      )
        return false;

      return true;
    };

    // Score
    if (!isVisibleByScore(lane.score, this.hlaScoreFilters)) {
      return false;
    }

    // Facility type
    if (
      attrFilters.facilityType.length > 0 &&
      !attrFilters.facilityType.includes(lane.facilityType as any)
    )
      return false;

    // Flow direction
    if (
      attrFilters.flowDirection.length > 0 &&
      !attrFilters.flowDirection.includes(lane.flowDirection as any)
    )
      return false;

    // Facility width per direction
    if (
      attrFilters.facilityWidthPerDirection.length > 0 &&
      !attrFilters.facilityWidthPerDirection.includes(
        lane.facilityWidthPerDirection as any,
      )
    )
      return false;

    // Light segregation
    if (
      attrFilters.lightSegregation.length > 0 &&
      !attrFilters.lightSegregation.includes(lane.lightSegregation as any)
    )
      return false;

    // Tram or train rails
    if (
      attrFilters.tramOrTrainRails.length > 0 &&
      !attrFilters.tramOrTrainRails.includes(lane.tramOrTrainRails as any)
    )
      return false;

    // Adjacent features
    if (
      !testAdjacentFeatures(lane.adjacentFeaturesLeft) &&
      !testAdjacentFeatures(lane.adjacentFeaturesRight)
    )
      return false;

    return true;
  }

  filterHlaWay(way: HlaWay) {
    if (!this.showHla) return false;
    const { surveyStore } = this.rootStore;
    const attrFilters = this.hlaAttributeFilters;

    // Verified
    if (
      this.hlaVerifiedFilters.length > 0 &&
      !this.hlaVerifiedFilters.includes(way.verified)
    )
      return false;

    // Type
    if (attrFilters.type.length > 0 && !attrFilters.type.includes(way.type))
      return false;

    // Heavy vehicle traffic
    if (
      attrFilters.heavyVehicleFlow.length > 0 &&
      !attrFilters.heavyVehicleFlow.includes(way.heavyVehicleFlow as any)
    )
      return false;

    // Average road speed
    const averageRoadSpeed =
      way.averageRoadSpeed ??
      getDefaultAverageRoadSpeed(way.type, surveyStore.distanceUnit);
    if (
      (attrFilters.averageRoadSpeed.min != null &&
        attrFilters.averageRoadSpeed.min >= averageRoadSpeed) ||
      (attrFilters.averageRoadSpeed.max != null &&
        attrFilters.averageRoadSpeed.max <= averageRoadSpeed)
    )
      return false;

    // Average daily traffic
    const averageDailyTraffic =
      way.averageDailyTraffic ?? getDefaultAverageDailyTraffic(way.type);
    if (
      (attrFilters.averageDailyTraffic.min != null &&
        attrFilters.averageDailyTraffic.min >= averageDailyTraffic) ||
      (attrFilters.averageDailyTraffic.max != null &&
        attrFilters.averageDailyTraffic.max <= averageDailyTraffic)
    )
      return false;

    // Street light
    if (attrFilters.lit.length > 0 && !attrFilters.lit.includes(way.lit))
      return false;

    return true;
  }

  isCraWayAvailableToUser(way: CraWay) {
    const { authStore } = this.rootStore;
    const hasCompletedCraWay = way.lifeCycleStage === LifeCycleStage.Completed;
    return authStore.isAuthenticated || hasCompletedCraWay;
  }

  isCraWayVisible(way: CraWay) {
    return (
      this.craWaysAvailableToUser &&
      this.showCra &&
      isLifeCycleStateVisibleThroughFilters(way, this.lifeCycleStageFilters) &&
      isCraWayVisibleThroughFilters(way.id, this.craWayFilters)
    );
  }

  isNodeVisibleByAttributes(craWayId: string, nodeId: string) {
    if (!this.craAttributeFilters.length) return true;

    const allNodeCodingsByWayId =
      this.originalOrForecastedAllNodeCodingsByWayId;
    const codingEntry = allNodeCodingsByWayId.get(craWayId)?.get(nodeId);
    if (!codingEntry) return true;

    return isNodeVisibleByAttributes(
      codingEntry.attributes,
      this.craAttributeFilters,
    );
  }

  isNodeVisibleByScore(score: Score | null) {
    return isVisibleByScore(score, this.craScoreFilters);
  }

  isHlaVisibleByScore(score: Score | null) {
    return isVisibleByScore(score, this.hlaScoreFilters);
  }

  isNodeVisibleByTreatment(nodeTreatments: Treatment[] | null) {
    return isNodeVisibleByTreatment(nodeTreatments, this.treatmentFilters);
  }

  setHlaVerifiedFilters(value: boolean[]) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.hlaVerifiedFilters = value;
    });
    this.filterHlaWays();
    mapStore.recenterMap();
  }

  setHlaAttributeFilters(value: HlaAttributeFilter) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.hlaAttributeFilters = value;
    });
    this.filterHlaWays();
    mapStore.recenterMap();
  }

  setAttributeFilters(value: CraAttributeFilter[]) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.craAttributeFilters = value;
    });
    this.filterNodes();
    mapStore.recenterMap();
  }

  setCraScoreFilters(value: ScoreFilters) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.craScoreFilters = value;
    });
    this.filterNodes();
    mapStore.recenterMap();
  }

  setHlaScoreFilters(value: ScoreFilters) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.hlaScoreFilters = value;
    });
    this.filterHlaWays();
    mapStore.recenterMap();
  }

  setShowHla(value: boolean) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.showHla = value;
    });
    this.filterHlaWays();
    mapStore.recenterMap();
  }

  setShowCra(value: boolean) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.showCra = value;
    });
    this.filterCraWays();
    mapStore.recenterMap();
  }

  setCraWayFilters(value: string[]) {
    const { mapStore } = this.rootStore;
    runInAction(() => {
      this.craWayFilters = value;
    });
    this.filterCraWays();
    this.filterHlaWays();
    mapStore.recenterMap();
  }

  setLifeCycleStageFilters(value: LifeCycleStage[]) {
    const { mapStore } = this.rootStore;
    if (this.rootStore.craForecastStore.isForecastModeActivated()) {
      this.forceCodedLifeCycleStageFilters(value);
    } else {
      runInAction(() => {
        this.lifeCycleStageFilters = value;
      });
      this.filterCraWays();
      this.filterHlaWays();
      mapStore.recenterMap();
    }
  }

  forceCodedLifeCycleStageFilters(value: LifeCycleStage[]) {
    runInAction(() => {
      // force selection of Review or Completed
      const codedStages = new Set(value);
      codedStages.delete(LifeCycleStage.Design);
      codedStages.delete(LifeCycleStage.DataCollection);
      codedStages.delete(LifeCycleStage.Coding);
      if (codedStages.size === 0) {
        codedStages.add(LifeCycleStage.Review);
        codedStages.add(LifeCycleStage.Completed);
      }
      this.lifeCycleStageFilters = [...codedStages];
    });
    this.filterCraWays();
    this.filterHlaWays();
  }

  setTreatmentFilters(value: TreatmentType[]) {
    runInAction(() => {
      this.treatmentFilters = value;
    });
    this.filterNodes();
    this.filterHlaWays();
  }
}
